import React, { useState, useCallback, useEffect, FormEvent } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Badge from '../common/Badge';
import { addPromoCode, removePromoCode } from '../../slices/cart';
import styles from './PromoCode.module.scss';
import messages from './messages';
import {
  ONLY_ONE_COUPON_ERROR,
  INVALID_OR_EXPIRED_COUPON_ERROR,
  COUPON_DOES_NOT_APPLY_ERROR,
  COUPON_ALREADY_USED_ERROR,
  COUPON_DOES_NOT_EXIST_ERROR,
  COUPON_HAS_BEEN_ALREADY_APPLIED_ERROR,
  EMPTY_COUPON_CODE_ERROR,
} from './constants';

import {
  NEWDAY_TPM_PROMO,
  ABANDONED_CART_PROMO_CODES_REGULAR_EXPRESSION,
  CONFLICTING_PROMO_TOASTER_TYPE_TO_SHOW_SESSION_STORAGE_KEY,
  CONFLICTING_PROMOS_TOAST_TYPE_10OFF_REMOVED,
} from '../../utils/common/constants';
import InputText from '../common/InputText';
import { CartInStore } from '../../slices/cart/types';
import { LineItemWithDiscounts } from './types';

const emptyCallback = () => {};

const getErrorMessage = (errorCode: string) => {
  switch (errorCode) {
    case ONLY_ONE_COUPON_ERROR:
      return messages.onlyOneCoupon;
    case INVALID_OR_EXPIRED_COUPON_ERROR:
      return messages.invalidOrExpiredCoupon;
    case COUPON_DOES_NOT_APPLY_ERROR:
      return messages.couponDoesNotApply;
    case COUPON_ALREADY_USED_ERROR:
      return messages.couponAlreadyUsed;
    case COUPON_DOES_NOT_EXIST_ERROR:
      return messages.couponDoesNotExist;
    case COUPON_HAS_BEEN_ALREADY_APPLIED_ERROR:
      return messages.couponHasAlreadyBeenApplied;
    case EMPTY_COUPON_CODE_ERROR:
      return messages.emptyCouponCodeError;
    default:
      return messages.couponDoesNotExist;
  }
};

const PromoCode = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const cart = useSelector((state: { cart: CartInStore }) => state.cart);
  const discountsApplied = useSelector((state: { cart: CartInStore }) => {
    const discounts = state.cart.lineItems
      .filter((lineItem) => lineItem.custom?.fields?.discounts_json)
      .flatMap((lineItem) =>
        (
          JSON.parse(lineItem.custom?.fields?.discounts_json as string) as {
            method: string;
            coupon_code: string;
            display_name?: string;
          }[]
        ).filter((discount) => discount.method === 'coupon'),
      )
      .map((discountJson) => ({
        coupon_code: discountJson.coupon_code,
        display_name: discountJson.display_name,
      }))
      .filter((discount) => discount.coupon_code !== NEWDAY_TPM_PROMO)
      .filter(
        (discount) =>
          !ABANDONED_CART_PROMO_CODES_REGULAR_EXPRESSION.test(
            discount.coupon_code,
          ),
      );

    const uniqueDiscounts = Array.from(
      new Map(
        discounts.map((discount) => [discount.coupon_code, discount]),
      ).values(),
    );

    return uniqueDiscounts as {
      coupon_code: string;
      display_name?: string;
    }[];
  });

  const abandonedCartDiscountsApplied = useSelector(
    (state: { cart: CartInStore }) => {
      const discounts = (state.cart.lineItems as LineItemWithDiscounts[])
        ?.filter((lineItem) => lineItem.custom?.fields?.discounts_json)
        .flatMap((lineItem) =>
          (
            JSON.parse(lineItem.custom?.fields?.discounts_json as string) as {
              method: string;
              coupon_code: string;
            }[]
          ).filter((discount) => discount.method === 'coupon'),
        )
        .map((discountJson) => discountJson.coupon_code)
        .filter((discount) => discount !== NEWDAY_TPM_PROMO)
        .filter((discount) =>
          ABANDONED_CART_PROMO_CODES_REGULAR_EXPRESSION.test(discount),
        );

      return [...new Set(discounts)] as string[];
    },
  );

  const [isExpanded, setExpanded] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState('');

  const setPromoCode = useCallback(
    (value: string) => {
      if (cart?.promoErrorCode) dispatch({ type: 'removePromoError' });
      setPromoCodeInput(value.trim().toUpperCase());
    },
    [cart?.promoErrorCode, dispatch],
  );

  const savePromoCode = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!promoCodeInput.length) {
        dispatch({
          type: 'addPromoError',
          errorCode: EMPTY_COUPON_CODE_ERROR,
        });
        return;
      }
      if (abandonedCartDiscountsApplied?.length) {
        // Using classic for because we need to wait until each promise is resolved (to get the new version of the cart)
        // eslint-disable-next-line no-restricted-syntax
        for (const abandonedCartDiscount of abandonedCartDiscountsApplied) {
          // eslint-disable-next-line no-await-in-loop
          await Promise.resolve(
            dispatch(removePromoCode(abandonedCartDiscount)),
          );
        }
        sessionStorage.setItem(
          CONFLICTING_PROMO_TOASTER_TYPE_TO_SHOW_SESSION_STORAGE_KEY,
          CONFLICTING_PROMOS_TOAST_TYPE_10OFF_REMOVED,
        );
      }
      dispatch(addPromoCode(promoCodeInput));
      if (promoCodeInput === discountsApplied[0]?.coupon_code) {
        setExpanded(false);
        setPromoCodeInput('');
      }
    },
    [abandonedCartDiscountsApplied, discountsApplied, dispatch, promoCodeInput],
  );

  const deletePromoCode = useCallback(
    (promoCode: string) => dispatch(removePromoCode(promoCode)),
    [dispatch],
  );

  useEffect(() => {
    if (discountsApplied?.length > 0) {
      setExpanded(false);
      setPromoCodeInput('');
    }
  }, [discountsApplied?.length]);

  useEffect(() => {
    if (isExpanded) {
      document.getElementById('promo-code')?.focus();
    }
  }, [isExpanded]);

  const tooglePromoCode = useCallback(() => {
    setExpanded((prevState) => !prevState);
    if (cart?.promoErrorCode) setPromoCode('');
  }, [cart?.promoErrorCode, setPromoCode]);

  return (
    <div className={styles['promo-code-container']}>
      <div className={styles['pricing-promo-code']}>
        <div className={styles['pricing-promo-code-title']}>
          {formatMessage(messages.pricingPromoCodeTitle)}
        </div>
        {!cart?.isDraft && discountsApplied.length === 0 && (
          <div
            onClick={tooglePromoCode}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                tooglePromoCode();
              }
            }}
            data-testid='add-button'
            role='link'
            className={styles['pricing-promo-code-amount']}
            tabIndex={0}
          >
            {!isExpanded
              ? formatMessage(messages.add)
              : formatMessage(messages.hide)}
          </div>
        )}
      </div>
      {isExpanded && !cart?.isDraft && discountsApplied.length === 0 && (
        <div>
          <form
            onSubmit={(e) => {
              savePromoCode(e);
            }}
            className={styles['pricing-promo-code-expanded']}
          >
            <InputText
              name='promoCode'
              id='promo-code'
              label={formatMessage(messages.pricingPromoCodeTitle)}
              value={promoCodeInput}
              onChange={(event) => setPromoCode(event.target.value)}
              onBlur={emptyCallback}
              isValid={!cart?.promoErrorCode}
              errorMsg={
                cart?.promoErrorCode
                  ? formatMessage(getErrorMessage(cart?.promoErrorCode), {
                      code: promoCodeInput,
                    })
                  : undefined
              }
              buttonText={formatMessage(messages.applyButton)}
            />
          </form>
        </div>
      )}
      {discountsApplied && discountsApplied.length > 0 && (
        <div
          className={styles['discounts-applied-container']}
          data-testid='discounts-container'
        >
          {discountsApplied.map((discount) => (
            <Badge
              text={discount.display_name ?? discount.coupon_code}
              key={discount.display_name ?? discount.coupon_code}
              isReadOnly={cart?.isDraft}
              onClickButton={() =>
                !cart?.isDraft
                  ? deletePromoCode(discount.coupon_code)
                  : undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PromoCode;
